/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {MouseEventHandler} from 'react';
import ReactPlayer from 'react-player';
import {SourceProps} from 'react-player/base';
import {makeStyles} from '@material-ui/core';
import {error} from '@ion/core/src/logger/logger';
import Render from '@ion/dls/src/render/Render';
import {useMedia} from '../../hooks/useMedia';
import {FullScreenIcon} from '../../icons/FullScreenIcon';
import {ErrorState} from '../../notifications/ErrorState';
import {dlsColor} from '../../theme/theme';
import {IconSize} from '../../types/types';
import {IconButton} from '../buttons/IconButton';
import OpenModal from '../buttons/OpenModal';
import {ButtonSpinner} from '../loading/ButtonSpinner';
import useVideoPlayer from './hooks/useVideoPlayer';
import PlayPause from './PlayPause';
import Volume from './Volume';

export interface VideoPlayerProps {
  handleOpenModal?: MouseEventHandler<HTMLButtonElement>;
  urls?: string | string[] | SourceProps[] | MediaStream;
  isControls?: boolean;
  isMiniPlayer?: boolean;
  isEventPopup?: boolean;
  openModalControl?: boolean;
  fullScreenControl?: boolean;
  audio?: boolean;
}

const useStyles = makeStyles((theme) => ({
  root : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'space-between'
  },
  controls : {
    backgroundColor : dlsColor('grey6'),
    marginTop       : ({isEventPopup}: {isEventPopup: boolean}) => isEventPopup ? '-3px' : '-5px',
    maxHeight       : 26,
    outline         : `1px solid ${dlsColor('grey5')}`,
    boxSizing       : 'border-box',
    padding         : '2px 12px 2px 8px'
  },
  parentDiv : {
    display        : 'flex',
    alignItems     : 'center',
    flexDirection  : 'row',
    justifyContent : 'center',
    transition     : 'all 400ms cubic-bezier(0.4, 0.0, 0.2, 1)'
  },
  videoplayer : {
    width  : '100%',
    height : '100%'
  },
  loader : {
    height          : '92px !important',
    width           : '100%',
    display         : 'flex',
    alignItems      : 'center',
    justifyContent  : 'center',
    backgroundColor : dlsColor('grey5')
  }
}));

/**
 * @MiniVideoPlayer
 *
 * Defines the Mini Video Player Component.
 * React-Player plugin is used for video player. It displays video recordings of
 * asset in a compact format.
 *
 * ```typescript
 *
 * import MiniVideoPlayer from '@ion/dls/src/components/videoplayer';
 *
 *   <MiniVideoPlayer urls={[videoSource]}
 *       handleOpenModal={eventClick}
 *       isMiniPlayer
 *       isControls />
 *
 * ```
*/
export const MiniVideoPlayer = ({
  urls,
  isControls,
  isEventPopup,
  handleOpenModal,
  openModalControl = true,
  fullScreenControl = false,
  audio = false
}: VideoPlayerProps) => {
  const {isPhone} = useMedia();
  const classes   = useStyles({isEventPopup});
  const {
    handleVolumeChange,
    handleVolumeIcon,
    handleVideoHover,
    handleVideoLeave,
    handlePlayPause,
    handleProgress,
    handleDuration,
    handleIsReady,
    handleEnded,
    isHovering,
    setFailed,
    duration,
    isReady,
    restart,
    playing,
    failed,
    volume,
    retry
  } = useVideoPlayer();

  let playerRef = null;

  const ref = videoplayer => {
    playerRef = videoplayer;
  };

  const handleFullscreen = async function() {
    const videoElement = playerRef?.getInternalPlayer?.();
    const api = videoElement.requestFullscreen ? 'requestFullscreen' : 'webkitEnterFullScreen';
    try {
      await videoElement[api]?.();
    } catch (err) {
      error('Error requesting fullscreen:', err);
    }
  };

  if (!failed) {
    return (
      <div className={classes.parentDiv}>
        <div id='videoplayer'
          className={classes.videoplayer}>
          {!isReady && (
            <div className={classes.loader}>
              <ButtonSpinner />
            </div>
          )}
          <div style={{display: isReady ? 'block' : 'none'}}
            onMouseEnter={handleVideoHover}
            onMouseLeave={handleVideoLeave}>
            <ReactPlayer ref={ref}
              url={urls[0]}
              playing={playing || isHovering}
              duration={duration}
              controls={isPhone}
              volume={audio ? volume : 0}
              width='100%'
              height='auto'
              onProgress={handleProgress}
              onDuration={handleDuration}
              onReady={handleIsReady}
              onEnded={handleEnded}
              onError={function() {
                setFailed(true);
              }} />
          </div>
          {isControls && !isPhone &&(
            <div className={`${classes.root} ${classes.controls}`}>
              <div className={classes.root}>
                <PlayPause playing={playing || isHovering}
                  restart={restart}
                  handlePlayPause={handlePlayPause}
                  size={22}
                  isCondensedVariant/>
              </div>
              <div className={classes.root}>
                <Render.If condition={audio}>
                  <Volume volume={volume}
                    handleVolumeChange={handleVolumeChange}
                    handleVolumeIcon={handleVolumeIcon}
                    displayRange={false}
                    size={20}/>
                </Render.If>
                {openModalControl && (
                  <OpenModal handleOpenModal={handleOpenModal}/>
                )}
                {fullScreenControl && (
                  <IconButton onClick={handleFullscreen} padding={'0px'} icon={<FullScreenIcon size={IconSize.small}/>} />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className={classes.root}>
      <ErrorState onRefresh={retry}
        isCondensedVariant
        show />
    </div>
  );
};

export default MiniVideoPlayer;
